import React from "react";
import styled from "styled-components";
import { Theme, colors } from "@literati/public-ui-shared-react";
import { NormalizedOrderSummary } from "components/Step/stepTypes";
import PaymentFormCoupon from "components/Payment/PaymentFormCoupon";
import { getNumericValueFromCurrencyString } from "components/Payment/paymentUtil";

const CLASS_BASE = "payment-order-summary";
export const PaymentOrderSummaryClassName = {
  BASE: CLASS_BASE,
  TRIGGER: `${CLASS_BASE}-trigger`,
  CONTENT: `${CLASS_BASE}-content`,
} as const;

const StyledLi = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
`;

export interface PaymentOrderSummaryProps {
  className?: string;
  giftCode?: string;
  orderSummary?: NormalizedOrderSummary | null;
  suppressCouponForm?: boolean;
}

export const PaymentOrderSummary = ({
  className = "",
  giftCode,
  orderSummary,
  suppressCouponForm = false,
}: PaymentOrderSummaryProps) => {
  const cls = PaymentOrderSummaryClassName;

  const {
    balanceDue,
    charge,
    coupon,
    credit,
    outstanding,
    shipping,
    subtotal,
    tax,
    total,
    voucherApplied,
  } = orderSummary || {};

  const shippingNumValue = getNumericValueFromCurrencyString(shipping?.value);
  const shippingValue =
    shippingNumValue === 0 || isNaN(shippingNumValue)
      ? "Free"
      : shipping?.value;

  const redeemingGift = Boolean(giftCode);
  const showCoupon =
    Math.abs(getNumericValueFromCurrencyString(coupon?.value)) > 0 &&
    !redeemingGift;
  const showCredit =
    Math.abs(getNumericValueFromCurrencyString(credit?.value)) > 0 &&
    !redeemingGift;

  const showOutstanding =
    getNumericValueFromCurrencyString(outstanding?.value) > 0;
  const showVoucher =
    Math.abs(getNumericValueFromCurrencyString(voucherApplied?.value)) > 0;
  const showBalanceDue =
    getNumericValueFromCurrencyString(balanceDue?.value) > 0;

  const showAlways = true;

  const lineItems = [
    [subtotal?.label, subtotal?.value, !!subtotal, "subtotal"],
    [tax?.label || "Sales Tax", tax?.value || "-----", showAlways],
    [shipping?.label || "Shipping", shippingValue || "Free", showAlways],
    [outstanding?.label, outstanding?.value, showOutstanding],
    [coupon?.label, coupon?.value, showCoupon, "coupon"],
    [voucherApplied?.label, voucherApplied?.value, showVoucher, "voucher"],
    [credit?.label, credit?.value, showCredit, "credit"],
    [balanceDue?.label, balanceDue?.value, showBalanceDue],
  ];

  return (
    <Theme>
      <div className={`${cls.BASE} ${className}`}>
        {!suppressCouponForm ? (
          <PaymentFormCoupon css="margin: 0 0 1rem" />
        ) : null}
        <ul className={cls.CONTENT}>
          {lineItems.map((li, idx) => {
            const [label, value, showItem, id] = li;
            return showItem && label && value ? (
              <StyledLi
                css={`
                  color: ${(id === "coupon" ||
                    id === "boxSavings" ||
                    id === "voucher" ||
                    id === "credit") &&
                  colors.palmGreen};
                  margin: ${idx === 0 ? "0" : "1.25rem 0 0"};
                `}
                key={`${label}-${idx}`}
              >
                <p>{label}</p>
                <p>{value}</p>
              </StyledLi>
            ) : null;
          })}
          {charge || total ? (
            <StyledLi
              css={`
                font-size: 0.875rem;
                font-weight: 500;
                margin: 1.25rem 0 0;
              `}
            >
              <p>Today’s Total</p>
              <p>{charge?.value || total?.value}</p>
            </StyledLi>
          ) : null}
        </ul>
      </div>
    </Theme>
  );
};

export default PaymentOrderSummary;
